/**
 * Quick-action gating + buttons for `InspectPopover`'s occupied-cell shell
 * (task 4.3, `unified-availability-interaction`). Split out of
 * `inspect-popover-parts.tsx` to keep both files under the file-size lint
 * budget after the force-release confirm step (offer-unified-table QA fix)
 * pushed the combined file over the cap.
 */
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@ooh/ui/components/button';
import type { BatchReleaseMode, BatchReleaseSelectedResponse, CellEntry } from '@ooh/contracts';
import { useSelectedRangeRelease } from '../../reservations/api/batch-mutations';
import {
  InspectActionConfirmStep,
  InspectReleaseChoiceConfirmStep,
  type ReleaseChoiceContext,
} from './inspect-quick-action-steps';
import type { ReleaseModeChoiceLabels } from '../../reservations/components/unified-select-release-mode-choice';
import { ReleaseSelectedResultView } from '../../reservations/components/unified-select-release-result-view';
import type { OutcomeResultUnitLookup } from '../../reservations/components/outcome-result-buckets';

// ─── Quick-action gating (permission-to-action mapping precedent: reservation-panel spec) ──

export interface QuickActionFlags {
  showZatwierdz: boolean;
  showSprzedaj: boolean;
  showAnuluj: boolean;
  showWymus: boolean;
  hasAnyAction: boolean;
}

export interface QuickActionPermissions {
  canUpgradeHard: boolean;
  canCloseSale: boolean;
  canCancel: boolean;
  canForceRelease: boolean;
}

// Ownership gate (CU-86cbc3r8b QA fix): upgrade/close-sale/cancel act on the HELD
// reservation via `assertHolderOrForce` — a non-holder can only reach them with
// force=true AND reservation.force_release. Gating on the permission alone (as
// before) showed these buttons on another user's hold to any canManage caller,
// and every click 403'd. Mirrors `reservation-list-view.tsx`'s
// `canAct = isOwn || permissions.canForceRelease` row-action precedent — the
// matching wire-level `force: true` is added at the handler call sites so the
// now-visible actions genuinely succeed instead of just being hidden less often.
// Extracted (rather than inlined) to keep `resolveQuickActionFlags` under the
// per-function complexity budget.
function canActOnHold(dominant: CellEntry, perms: QuickActionPermissions): boolean {
  return dominant.ownership === 'mine' || perms.canForceRelease;
}

function anyActionVisible(flags: Omit<QuickActionFlags, 'hasAnyAction'>): boolean {
  return flags.showZatwierdz || flags.showSprzedaj || flags.showAnuluj || flags.showWymus;
}

export function resolveQuickActionFlags(
  dominant: CellEntry,
  perms: QuickActionPermissions,
): QuickActionFlags {
  const isActiveHold =
    dominant.status === 'SOFT_RESERVATION' || dominant.status === 'HARD_RESERVATION';
  const canAct = canActOnHold(dominant, perms);
  const showZatwierdz = canAct && perms.canUpgradeHard && dominant.status === 'SOFT_RESERVATION';
  const showSprzedaj = canAct && perms.canCloseSale && isActiveHold;
  // SOLD is never touchable — matches the batch-release "SOLD is never touchable" decision.
  const showAnuluj = canAct && perms.canCancel && dominant.status !== 'SOLD';
  const showWymus = perms.canForceRelease && dominant.status !== 'SOLD';
  const flags = { showZatwierdz, showSprzedaj, showAnuluj, showWymus };
  return { ...flags, hasAnyAction: anyActionVisible(flags) };
}

export interface InspectQuickActionsProps {
  testId: string;
  flags: QuickActionFlags;
  openInPanelHref: string | null;
  /** The held reservation's holder name (already gated by `hasLabelAuthorization` by the
   * caller) — names the affected hold in the force-release confirm step below. */
  holder?: string;
  /** The inspected cell's own media-unit display label — names the nośnik in the
   * mode='selected' release result view below. */
  unitLabel?: string;
  /** Gates the Anuluj/Wymuś whole-versus-selected-period choice — see its own doc comment. */
  releaseChoice?: ReleaseChoiceContext;
  onUpgradeHard?: () => void;
  onCloseSale?: () => void;
  onCancel?: () => void;
  onForceRelease?: () => void;
}

export function InspectQuickActions({
  testId,
  flags,
  openInPanelHref,
  holder,
  unitLabel,
  releaseChoice,
  onUpgradeHard,
  onCloseSale,
  onCancel,
  onForceRelease,
}: InspectQuickActionsProps) {
  const { t } = useTranslation('reservations');
  const [confirmingSale, setConfirmingSale] = React.useState(false);
  const [confirmingForceRelease, setConfirmingForceRelease] = React.useState(false);
  const [confirmingCancel, setConfirmingCancel] = React.useState(false);
  const [releaseMode, setReleaseMode] = React.useState<BatchReleaseMode | null>(null);
  const [releaseResult, setReleaseResult] = React.useState<{
    response: BatchReleaseSelectedResponse;
    reservationId: number;
    mediaUnitId: number;
  } | null>(null);
  const { releaseSelectedRange, isPending: isReleasePending } = useSelectedRangeRelease();

  const releaseModeChoiceLabels: ReleaseModeChoiceLabels = {
    title: t('actions.releaseModeChoice.title'),
    wholeLabel: t('actions.releaseModeChoice.wholeLabel'),
    selectedLabel: t('actions.releaseModeChoice.selectedLabel'),
  };

  function resetReleaseChoiceState() {
    setConfirmingCancel(false);
    setConfirmingForceRelease(false);
    setReleaseMode(null);
  }

  /** Commits the chosen release mode: 'full' defers to the caller's existing
   * onCancel/onForceRelease (unchanged whole-release path); 'selected' calls the same
   * `POST /reservations/batch-release` `UnifiedSelectPopover` uses, for just the
   * inspected cell's own date range. */
  function commitRelease(kind: 'cancel' | 'forceRelease') {
    if (!releaseChoice || releaseMode === null) return;
    if (releaseMode === 'full') {
      resetReleaseChoiceState();
      if (kind === 'cancel') onCancel?.();
      else onForceRelease?.();
      return;
    }
    const { reservationId, mediaUnitId, cellDateFrom, cellDateTo } = releaseChoice;
    releaseSelectedRange(
      { reservationId, mediaUnitId, dateFrom: cellDateFrom, dateTo: cellDateTo },
      (response) => {
        resetReleaseChoiceState();
        setReleaseResult({ response, reservationId, mediaUnitId });
      },
    );
  }

  /** Dispatches to whichever confirm/result step is active, or `null` for the default
   * button row — a separate function (own complexity budget) so `InspectQuickActions`
   * itself stays under its complexity limit despite the added release-choice branches. */
  function renderConfirmStep(): React.ReactNode | null {
    if (releaseResult) {
      const reservationUnitLookup = new Map([
        [releaseResult.reservationId, releaseResult.mediaUnitId],
      ]);
      const unitLookup: OutcomeResultUnitLookup = new Map(
        unitLabel
          ? [[releaseResult.mediaUnitId, { grwNumber: unitLabel, address: null, city: null }]]
          : [],
      );
      return (
        <ReleaseSelectedResultView
          response={releaseResult.response}
          reservationUnitLookup={reservationUnitLookup}
          unitLookup={unitLookup}
          onDismiss={() => setReleaseResult(null)}
        />
      );
    }

    if (confirmingSale) {
      return (
        <InspectActionConfirmStep
          namePrefix={`${testId}-close-sale`}
          body={t('dialogs.closeSaleBody')}
          confirmVariant="success"
          onCancel={() => setConfirmingSale(false)}
          onConfirm={() => {
            setConfirmingSale(false);
            onCloseSale?.();
          }}
        />
      );
    }

    // Forcing a release takes another user's hold away (spec "Forcing a release still
    // confirms first") — names the affected hold's holder, falling back to a generic
    // label when the caller lacks hasLabelAuthorization (an independent gate from
    // canForceRelease — see `resolveOccupiedCellView`).
    if (confirmingForceRelease) {
      const forceReleaseBody = t('actions.forceReleaseConfirmBody', {
        holder: holder ?? t('actions.forceReleaseConfirmUnknownHolder'),
      });
      if (releaseChoice) {
        return (
          <InspectReleaseChoiceConfirmStep
            namePrefix={`${testId}-force-release`}
            body={forceReleaseBody}
            confirmVariant="destructive"
            mode={releaseMode}
            onModeChange={setReleaseMode}
            labels={releaseModeChoiceLabels}
            pending={isReleasePending}
            onCancel={resetReleaseChoiceState}
            onConfirm={() => commitRelease('forceRelease')}
          />
        );
      }
      return (
        <InspectActionConfirmStep
          namePrefix={`${testId}-force-release`}
          body={forceReleaseBody}
          confirmVariant="destructive"
          onCancel={() => setConfirmingForceRelease(false)}
          onConfirm={() => {
            setConfirmingForceRelease(false);
            onForceRelease?.();
          }}
        />
      );
    }

    // Anuluj always confirms first (spec "the action SHALL proceed as a single
    // confirmation ... exactly as before this capability existed", matching Wymuś
    // zwolnienie right next to it) — branches on `releaseChoice` for which confirm
    // shape applies, same as the Wymuś zwolnienie branch above.
    if (confirmingCancel) {
      if (releaseChoice) {
        return (
          <InspectReleaseChoiceConfirmStep
            namePrefix={`${testId}-cancel`}
            confirmVariant="destructive"
            mode={releaseMode}
            onModeChange={setReleaseMode}
            labels={releaseModeChoiceLabels}
            pending={isReleasePending}
            onCancel={resetReleaseChoiceState}
            onConfirm={() => commitRelease('cancel')}
          />
        );
      }
      return (
        <InspectActionConfirmStep
          namePrefix={`${testId}-cancel`}
          body={t('dialogs.cancelConfirmBody')}
          confirmVariant="destructive"
          onCancel={() => setConfirmingCancel(false)}
          onConfirm={() => {
            setConfirmingCancel(false);
            onCancel?.();
          }}
        />
      );
    }

    return null;
  }

  const confirmStep = renderConfirmStep();
  if (confirmStep) return confirmStep;

  return (
    <div className="flex flex-col gap-1.5 w-full" data-testid={`${testId}-actions`}>
      {flags.hasAnyAction ? (
        <div className="flex flex-wrap gap-1.5">
          {flags.showZatwierdz ? (
            <Button
              size="xs"
              variant="outline"
              data-testid={`${testId}-upgrade-hard-btn`}
              onClick={onUpgradeHard}
            >
              {t('actions.upgradeHard')}
            </Button>
          ) : null}
          {flags.showSprzedaj ? (
            <Button
              size="xs"
              variant="success"
              data-testid={`${testId}-close-sale-btn`}
              onClick={() => setConfirmingSale(true)}
            >
              {t('actions.closeSale')}
            </Button>
          ) : null}
          {flags.showAnuluj ? (
            <Button
              size="xs"
              variant="destructive"
              data-testid={`${testId}-cancel-btn`}
              onClick={() => setConfirmingCancel(true)}
            >
              {t('actions.cancel')}
            </Button>
          ) : null}
          {flags.showWymus ? (
            <Button
              size="xs"
              variant="ghost"
              data-testid={`${testId}-force-release-btn`}
              onClick={() => setConfirmingForceRelease(true)}
            >
              {t('actions.forceRelease')}
            </Button>
          ) : null}
        </div>
      ) : null}
      {openInPanelHref ? (
        <a
          href={openInPanelHref}
          className="text-xs text-primary underline hover:no-underline"
          data-testid={`${testId}-open-in-panel-link`}
        >
          {t('actions.openInPanel')}
        </a>
      ) : null}
    </div>
  );
}
